import './styles/Button.scss';

import { CaretRightIcon as CaretRight, Check } from '@phosphor-icons/react';
import React, {
  HTMLAttributes,
  PropsWithChildren,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  cn,
  getClassnamesBasedOnCondition,
  getClassOrDefault,
} from '../../helpers';
import { useClassHelper } from '../../hooks';
import { Loading } from '../Loading';

const className = 'p-Button';

const SUCCESS_MESSAGE_FADEOUT_TIME = 2000;

const getDataProps = (props: any) => {
  const data = Object.keys(props)
    .filter(key => key.indexOf('data-') === 0)
    .reduce((a, b) => {
      a[b] = props[b];
      return a;
    }, {});

  return data;
};

// Heights live here (Tailwind); the BEM size modifier (p-Button--md, …) is
// still emitted for external selectors that hook it (e.g. DateRangePicker).
const sizeClasses = {
  /** 32px */
  xs: 'h-8',
  /** 36px */
  md: 'h-9',
  /** 40px (default) */
  lg: 'h-10',
} as const;

const getIconSize = (size: string) => {
  switch (size) {
    case 'xs':
      return 14;
    case 'lg':
      return 18;
    default:
      return 16;
  }
};

interface Props extends PropsWithChildren, HTMLAttributes<HTMLButtonElement> {
  disabled?: boolean;
  fluid?: boolean;
  rounded?: boolean;
  /**
   * Button height:
   * - `xs` → 32px
   * - `md` → 36px
   * - `lg` → 40px (default)
   *
   * Also scales the caret/check icons (14/16/18px).
   */
  size?: keyof typeof sizeClasses;
  ghost?: any;
  color?:
    | 'primary'
    | 'secondary'
    | 'tertiary'
    | 'disabled'
    | 'danger'
    | 'ghost';
  id?: string;
  type?: 'button' | 'submit' | 'reset';
  arrow?: boolean;
  shortArrow?: boolean;
  classes?: {
    button?: string;
    content?: string;
  };
  loading?: boolean;
  successMessage?: string;
  onFadeOutMessage?: () => void;
}

export const Button = (props: Props) => {
  const {
    onClick,
    children,
    disabled,
    fluid,
    rounded,
    size = 'lg',
    color,
    id,
    type = 'button',
    arrow,
    shortArrow,
    loading,
    classes,
    successMessage,
    onFadeOutMessage = () => undefined,
    ...rest
  } = props;

  const [showSuccessMessage, setShowHideSuccessMessage] = useState(true);
  const [disabledByAction, setDisabledByAction] = useState(false);
  const iconSize = getIconSize(size);
  const c = useClassHelper(className);

  const buttonDisabled = useMemo(() => {
    return disabled || !!loading || !!successMessage || disabledByAction;
  }, [disabled, loading, successMessage, disabledByAction]);

  useEffect(() => {
    if (successMessage) {
      setShowHideSuccessMessage(true);
      const timeout = setTimeout(() => {
        setShowHideSuccessMessage(false);
        onFadeOutMessage();
      }, SUCCESS_MESSAGE_FADEOUT_TIME);

      return () => clearTimeout(timeout);
    }
  }, [successMessage]);
  const handleOnClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    if (buttonDisabled) {
      e.preventDefault();
      return;
    }

    if (
      onClick?.constructor?.name === 'AsyncFunction' ||
      onClick instanceof Promise
    ) {
      setDisabledByAction(true);
      await onClick(e);
      setDisabledByAction(false);
      return;
    }

    if (onClick) {
      onClick(e);
    }
  };

  const { button: buttonClassName, content: buttonContentClassName } =
    classes || {};

  const dataProps = getDataProps(props);

  const activeClassNames = c(
    getClassnamesBasedOnCondition(fluid, '--fluid'),
    getClassnamesBasedOnCondition(rounded, '--rounded'),
    getClassOrDefault(size, '--md'),
    getClassOrDefault(color, '--primary'),
  );

  const renderContent = () => {
    if (loading) {
      return (
        <>
          <div className={`${className}__loading`}>
            <Loading fill={'#fff'} size={18} aria-label="loading icon" />
          </div>
        </>
      );
    }

    if (successMessage && showSuccessMessage) {
      return (
        <>
          <div className={`${className}__save`}>
            <Check size={iconSize} />
          </div>
          {successMessage}
        </>
      );
    }

    return children;
  };

  return (
    <button
      aria-disabled={buttonDisabled}
      aria-label={buttonDisabled ? 'Disabled' : undefined}
      // TODO - Remove disabled - This will be fixed with Chakra-UI integration
      /**
       * Description: WHY
       * This prevents any interaction with the button, so multiple requests won’t be sent.
       * However, disabled may also be problematic for screenreaders. When a button is disabled, it's removed from the tab navigation.
       * This require if a keyboard user is tab-ing through a page, a disabled button will not be focused.
       * More: https://www.bekk.christmas/post/2023/24/accessible-loading-button
       */
      disabled={buttonDisabled}
      tabIndex={0}
      type={type}
      id={id}
      {...dataProps}
      {...rest}
      className={cn(
        sizeClasses[size || 'md'],
        rest.className ||
          `${className} ${activeClassNames} ${buttonClassName || ''}`,
        'focus-visible:focus-ring',
      )}
      onClick={handleOnClick}
    >
      <div
        className={`${className}__content${
          buttonContentClassName ? ` ${buttonContentClassName}` : ''
        }`}
      >
        {renderContent()}
        {arrow && (
          <div className={c('__icon')}>
            <CaretRight size={iconSize} />
          </div>
        )}
        {shortArrow && (
          <div className={c('__shortArrow')}>
            <CaretRight size={iconSize} />
          </div>
        )}
      </div>
    </button>
  );
};
